// What a session that fell back to localStorage did, and the rules for reading
// it. Pure: no storage and no baseline here, so the rules can be read — and
// tested — on their own, the same way services/storage/rows.ts is. The engine
// that keeps one lives in services/storage/fallback.ts.
//
// A delta is keyed by row id, so touching one row twice leaves one entry and
// the last word wins. Insertion order is the order the rows were first
// touched, which is the order a replay should follow.

export type Change = { op: 'put', row: { id: string } } | { op: 'remove' }
export type Delta = Record<string, Change>

// What a fallback session should show: the rows it can still read, with
// everything this session has done to them laid over the top.
export function applied<T extends { id: string }>(rows: T[], delta: Delta): T[] {
    const out: T[] = []
    for (const row of rows) {
        const change = delta[row.id]
        if (!change) out.push(row)
        else if (change.op === 'put') out.push(change.row as T)
        // A removed row is left out, which is the whole of a deletion here.
    }
    // Then the rows this session made, which no snapshot can know about.
    const known = new Set(rows.map(row => row.id))
    for (const [id, change] of Object.entries(delta)) {
        if (change.op === 'put' && !known.has(id)) out.push(change.row as T)
    }
    return out
}

export const withPut = <T extends { id: string }>(delta: Delta, row: T): Delta =>
    ({ ...delta, [row.id]: { op: 'put', row } })

// A tombstone even for a row this session invented: replaying a delete of
// something that was never stored costs nothing, and working out which case it
// is would mean remembering more than the last word.
export const withRemove = (delta: Delta, id: string): Delta =>
    ({ ...delta, [id]: { op: 'remove' } })

// Anything unrecognisable in storage is dropped rather than trusted — the same
// treatment rowsFrom gives the legacy arrays.
export function deltaFrom(stored: unknown): Delta {
    if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return {}
    const out: Delta = {}
    for (const [id, change] of Object.entries(stored as Record<string, unknown>)) {
        if (!change || typeof change !== 'object') continue
        const { op, row } = change as { op?: unknown, row?: unknown }
        if (op === 'remove') {
            out[id] = { op: 'remove' }
        } else if (op === 'put' && row && typeof row === 'object'
            && typeof (row as { id?: unknown }).id === 'string') {
            out[id] = { op: 'put', row: row as { id: string } }
        }
    }
    return out
}
